// 2 decimal places in the percent display => 1e-4 precision as a fraction of 1.
const FRACTION_PRECISION = 10000;
// Display percents use 2 decimal places => 100ths of a percent point.
const PERCENT_CENTS_PER_POINT = 100;

/**
 * Largest-remainder apportionment of `values` (weighted by `weightTotal`) into
 * `unitCount` indivisible units. Returns non-negative integers that sum to
 * `unitCount` (or zeros when inputs are empty/invalid).
 */
export function apportionUnits(
  values: number[],
  weightTotal: number,
  unitCount: number,
): number[] {
  if (weightTotal === 0 || values.length === 0 || unitCount <= 0) {
    return values.map(() => 0);
  }

  const exact = values.map((v) => (v / weightTotal) * unitCount);
  const floored = exact.map((v) => Math.floor(v));
  const remainder = unitCount - floored.reduce((sum, v) => sum + v, 0);
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);

  for (let k = 0; k < remainder; k++) {
    floored[order[k % order.length].i] += 1;
  }

  return floored;
}

/**
 * Largest-remainder apportionment so rounded fractions sum to exactly 1
 * (100%) instead of drifting from independent rounding.
 * Returns fractions in [0, 1] at 0.01%-of-whole precision (1e-4).
 */
export const percentageBreakdown = (
  values: number[],
  total: number,
): number[] => {
  return apportionUnits(values, total, FRACTION_PRECISION).map(
    (units) => units / FRACTION_PRECISION,
  );
};

/**
 * Largest-remainder apportionment of `weights` into display percents that sum
 * exactly to `targetPercent` at 2 decimal places (e.g. parent 50 → children
 * summing to 50.00, not 50.01).
 */
export function apportionPercents(
  weights: number[],
  targetPercent: number,
): number[] {
  if (weights.length === 0) {
    return [];
  }

  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  const targetCents = Math.round(targetPercent * PERCENT_CENTS_PER_POINT);
  if (weightTotal === 0 || targetCents <= 0) {
    return weights.map(() => 0);
  }

  return apportionUnits(weights, weightTotal, targetCents).map(
    (cents) => cents / PERCENT_CENTS_PER_POINT,
  );
}

/**
 * Clamps a display percent to [0, 100]. Non-finite values (NaN, ±Infinity)
 * become 0 so progress bars and captions never receive invalid widths.
 */
export function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

export const getPercentChangeBetweenTwoNumbers = (
  startingValue: number,
  endingValue: number,
): number => {
  if (startingValue === 0 && endingValue === 0) {
    return 0;
  } else if (endingValue === 0) {
    return startingValue > 0 ? -100 : 100;
  } else if (startingValue === 0) {
    return endingValue > 0 ? 100 : -100;
  } else {
    return ((endingValue - startingValue) / Math.abs(startingValue)) * 100;
  }
};
